import { useParams, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import {
  useGetWorkOrderById,
  useStartWorkOrder,
  useGetWorkOrderHistory,
} from "@/hooks/useWorkOrdersApi";
import { useGeneratePresignedUrl } from "@/hooks/useMedia";
import {
  useGetComments,
  useCreateComment,
  type Comment,
} from "@/hooks/useComments";
import { useGetCurrentUser } from "@/hooks/useUser";
import { Spinner } from "@/components/Spinner";
import { WorkOrderMap } from "@/components/WorkOrderMap";
import { ImageLightbox } from "@/components/ImageLightbox";
import {
  IoArrowBack,
  IoLocationOutline,
  IoCalendarOutline,
  IoTimeOutline,
  IoPersonOutline,
  IoPeopleOutline,
  IoDocumentTextOutline,
  IoCheckmarkCircle,
  IoChatbubbleEllipsesOutline,
  IoSendOutline,
  IoAttachOutline,
  IoCloseOutline,
  IoDocumentAttachOutline,
  IoPlayCircleOutline,
  IoCheckmarkDoneCircle,
  IoNavigateOutline,
} from "react-icons/io5";
import { getTimeAgo } from "@/utils/getTimeAgo";
import { reverseGeocode, isCoordinateAddress } from "@/utils/reverseGeocode";
import { motion } from "framer-motion";
import toast from "react-hot-toast";

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "URGENT":
      return "bg-red-500 text-white border-red-600";
    case "HIGH":
      return "bg-orange-500 text-white border-orange-600";
    case "MEDIUM":
      return "bg-yellow-500 text-white border-yellow-600";
    case "LOW":
      return "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]";
    default:
      return "bg-gray-500 text-white border-gray-600";
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "IN_PROGRESS":
      return "bg-[hsl(var(--secondary))]/10 text-[hsl(var(--secondary))]";
    case "PENDING":
      return "bg-yellow-100 text-yellow-700";
    case "AWAITING_VALIDATION":
      return "bg-purple-100 text-purple-700";
    case "COMPLETED":
      return "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]";
    case "CANCELLED":
      return "bg-gray-100 text-gray-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case "IN_PROGRESS":
      return "Em Andamento";
    case "PENDING":
      return "Pendente";
    case "AWAITING_VALIDATION":
      return "Aguardando Validação";
    case "COMPLETED":
      return "Concluída";
    case "CANCELLED":
      return "Cancelada";
    case "RESOLVED":
      return "Resolvida";
    case "FILED":
      return "Arquivada";
    default:
      return status;
  }
};

// Função para traduzir valores do histórico que vêm da API
const translateHistoryValue = (value: string | null): string => {
  if (!value) return "";

  // Se o valor começar com "Status: ", extrair e traduzir o status
  if (value.startsWith("Status: ")) {
    const status = value.replace("Status: ", "");
    return `Status: ${getStatusLabel(status)}`;
  }

  // Se for um status direto (PENDING, IN_PROGRESS, etc), traduzir
  const possibleStatuses = [
    "PENDING",
    "IN_PROGRESS",
    "AWAITING_VALIDATION",
    "COMPLETED",
    "CANCELLED",
    "RESOLVED",
    "FILED",
  ];
  if (possibleStatuses.includes(value)) {
    return getStatusLabel(value);
  }

  // Para outros valores, retornar como está
  return value;
};

export const WorkOrderDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: workOrder, isLoading } = useGetWorkOrderById(id || "");

  // Mutations da API
  const startWorkOrder = useStartWorkOrder();
  const generatePresignedUrl = useGeneratePresignedUrl();

  // Estado do chat interno (comentários por ocorrência)
  const [activeCommentTab, setActiveCommentTab] = useState<string>(""); // ID da ocorrência ativa
  const [messageContent, setMessageContent] = useState("");
  const [messageFiles, setMessageFiles] = useState<File[]>([]);
  const [isSubmittingMessage, setIsSubmittingMessage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Usuário atual para identificar mensagens próprias
  const { data: currentUser } = useGetCurrentUser();
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Buscar comentários da ocorrência ativa
  const { data: commentsData } = useGetComments({
    occurrenceId: activeCommentTab,
    type: "INTERNAL",
    limit: 50,
  });
  const createComment = useCreateComment();

  // Estado do modal de finalização
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [finishDescription, setFinishDescription] = useState("");
  const [finishFiles, setFinishFiles] = useState<File[]>([]);
  const [isSubmittingFinish, setIsSubmittingFinish] = useState(false);
  const finishFileInputRef = useRef<HTMLInputElement>(null);

  // Estado do menu de navegação
  const [showNavMenu, setShowNavMenu] = useState<string | null>(null);

  // Estado do lightbox de imagens
  const [lightboxImages, setLightboxImages] = useState<{ url: string; alt?: string }[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const openLightbox = (media: { id: string; key: string; type: string }[], startIndex: number) => {
    const imageMedia = media.filter((m) => m.type === "IMAGE");
    setLightboxImages(
      imageMedia.map((m) => ({
        url: `${import.meta.env.VITE_BUCKET_URL}/${m.key}`,
        alt: "Foto da ocorrência",
      }))
    );
    setLightboxIndex(startIndex);
    setIsLightboxOpen(true);
  };

  // Endereços resolvidos via reverse geocoding
  const [resolvedAddresses, setResolvedAddresses] = useState<Record<string, string>>({});

  // Resolver endereços apenas quando a API retorna coordenadas no campo address
  useEffect(() => {
    if (!workOrder?.items) return;

    workOrder.items.forEach((item) => {
      const occ = item.occurrence;
      if (
        isCoordinateAddress(occ.address) &&
        occ.latitude &&
        occ.longitude &&
        !resolvedAddresses[occ.id]
      ) {
        const lat = parseFloat(occ.latitude);
        const lng = parseFloat(occ.longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          reverseGeocode(lat, lng).then((addr) => {
            setResolvedAddresses((prev) => ({ ...prev, [occ.id]: addr }));
          });
        }
      }
    });
  }, [workOrder?.items]);

  // Auto-scroll para última mensagem do chat
  useEffect(() => {
    const el = chatContainerRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }
  }, [commentsData]);

  // Função para obter duração do vídeo
  const getVideoDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.preload = "metadata";

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };

      video.onerror = () => {
        reject(new Error("Erro ao carregar vídeo"));
      };

      video.src = URL.createObjectURL(file);
    });
  };

  // Handlers para anexos
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter((file) => file.size <= 20 * 1024 * 1024);

    if (validFiles.length < files.length) {
      toast.error("Alguns arquivos foram ignorados por excederem 20MB");
    }

    setMessageFiles((prev) => [...prev, ...validFiles]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    setMessageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitMessage = async () => {
    if (!messageContent.trim() || !activeCommentTab) {
      toast.error("Selecione uma ocorrência para comentar");
      return;
    }

    setIsSubmittingMessage(true);

    try {
      // Upload dos arquivos anexados
      const uploadedAttachments: Array<{
        key: string;
        fileName: string;
        mimeType: string;
        size: number;
      }> = [];

      for (const file of messageFiles) {
        const isImage = file.type.startsWith("image/");
        const mediaType = isImage ? "IMAGE" : "VIDEO";

        const presignedData = await generatePresignedUrl.mutateAsync({
          fileName: file.name,
          mimeType: file.type,
          type: mediaType,
          size: file.size,
        });

        await fetch(presignedData.uploadUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        });

        uploadedAttachments.push({
          key: presignedData.key,
          fileName: file.name,
          mimeType: file.type,
          size: file.size,
        });
      }

      // Criar comentário interno
      await createComment.mutateAsync({
        occurrenceId: activeCommentTab,
        data: {
          type: "INTERNAL",
          content: messageContent,
          attachments:
            uploadedAttachments.length > 0 ? uploadedAttachments : undefined,
        },
      });

      toast.success("Comentário enviado com sucesso!");
      setMessageContent("");
      setMessageFiles([]);
    } catch (error) {
      toast.error("Erro ao enviar comentário");
      console.error(error);
    } finally {
      setIsSubmittingMessage(false);
    }
  };

  const handleStartWorkOrder = async () => {
    if (!id) return;

    try {
      await startWorkOrder.mutateAsync(id);
      toast.success("Ordem de Serviço iniciada!");
    } catch (error) {
      toast.error("Erro ao iniciar Ordem de Serviço");
      console.error(error);
    }
  };

  const handleFinishFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles: File[] = [];
    
    for (const file of files) {
      // Validar tipo: apenas imagens e vídeos
      if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
        toast.error(`"${file.name}" não é permitido. Apenas imagens e vídeos.`);
        continue;
      }
      
      // Validar tamanho: máximo 20MB
      if (file.size > 20 * 1024 * 1024) {
        toast.error(`"${file.name}" excede o tamanho máximo de 20MB`);
        continue;
      }
      
      // Validar duração do vídeo: máximo 10 segundos
      if (file.type.startsWith("video/")) {
        try {
          const duration = await getVideoDuration(file);
          if (duration >= 11) {
            toast.error(
              `Vídeo "${file.name}" excede o limite de 10 segundos (${Math.round(duration)}s)`
            );
            continue;
          }
        } catch {
          toast.error(`Erro ao processar vídeo "${file.name}"`);
          continue;
        }
      }
      
      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setFinishFiles((prev) => [...prev, ...validFiles]);
    }

    if (finishFileInputRef.current) {
      finishFileInputRef.current.value = "";
    }
  };

  const handleRemoveFinishFile = (index: number) => {
    setFinishFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFinishWorkOrder = async () => {
    if (!id) return;

    if (!finishDescription.trim()) {
      toast.error("Por favor, adicione uma descrição");
      return;
    }

    if (finishFiles.length === 0) {
      toast.error("Por favor, anexe pelo menos um comprovante");
      return;
    }

    setIsSubmittingFinish(true);

    try {
      // Upload dos arquivos
      const uploadedKeys: string[] = [];

      for (const file of finishFiles) {
        // Determinar tipo de mídia
        const isImage = file.type.startsWith("image/");
        const mediaType = isImage ? "IMAGE" : "VIDEO";

        // Gerar URL pré-assinada
        const presignedData = await generatePresignedUrl.mutateAsync({
          fileName: file.name,
          mimeType: file.type,
          type: mediaType,
          size: file.size,
        });

        // Upload para R2
        await fetch(presignedData.uploadUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        });

        uploadedKeys.push(presignedData.key);
      }

      // Submeter OS para validação - usando fetch direto
      const token = localStorage.getItem("duas-vidas-token");
      const baseUrl = import.meta.env.VITE_BASE_URL || "http://localhost:3000";
      
      const response = await fetch(`${baseUrl}/work-orders/${id}/evaluate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          completionNotes: finishDescription,
          attachments: uploadedKeys,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Erro ao enviar OS para validação");
      }

      // Sucesso - fechar modal e navegar
      setFinishDescription("");
      setFinishFiles([]);
      setIsSubmittingFinish(false);
      setShowFinishModal(false);
      toast.success("OS enviada para validação com sucesso!");
      navigate("/ocurrences");
      
    } catch (error: any) {
      console.error("Erro ao enviar OS para validação:", error);
      toast.error(error?.message || "Erro ao enviar OS para validação");
      setIsSubmittingFinish(false);
    }
  };

  // Funções de navegação
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
  };

  const openNavigation = (
    lat: string,
    lng: string,
    address: string,
    app: "google" | "waze" | "apple",
  ) => {
    const latitude = parseFloat(lat);
    const longitude = parseFloat(lng);
    const encodedAddress = encodeURIComponent(address);

    let url = "";

    switch (app) {
      case "google":
        // Google Maps funciona em todos os dispositivos
        url = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
        break;
      case "waze":
        // Waze deep link
        url = `https://waze.com/ul?ll=${latitude},${longitude}&navigate=yes&zoom=17`;
        break;
      case "apple":
        // Apple Maps (funciona no iOS e macOS)
        url = `http://maps.apple.com/?daddr=${latitude},${longitude}&q=${encodedAddress}`;
        break;
    }

    window.open(url, "_blank");
    setShowNavMenu(null);
  };

  // Fechar menu de navegação ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showNavMenu &&
        !(event.target as Element).closest(".nav-menu-container")
      ) {
        setShowNavMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showNavMenu]);

  // Definir primeira ocorrência como aba ativa por padrão
  useEffect(() => {
    if (workOrder && workOrder.items.length > 0 && !activeCommentTab) {
      setActiveCommentTab(workOrder.items[0].occurrence.id);
    }
  }, [workOrder, activeCommentTab]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!workOrder) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          Ordem de Serviço não encontrada
        </h2>
        <button
          onClick={() => navigate("/ocurrences")}
          className="text-[hsl(var(--primary))] hover:underline"
        >
          Voltar para listagem
        </button>
      </div>
    );
  }

  const isOverdue =
    workOrder.estimatedEndDate &&
    new Date(workOrder.estimatedEndDate) < new Date();

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] text-white p-6 shadow-md mt-16"
      >
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 mb-4 hover:opacity-80 transition-opacity"
        >
          <IoArrowBack size={24} />
          <span className="font-semibold">Voltar</span>
        </button>

        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium opacity-90">
              {workOrder.osNumber}
            </span>
            <h1 className="text-2xl font-bold mt-1 mb-3 break-words">{workOrder.title}</h1>
            <div className="flex flex-wrap gap-2">
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold bg-white/20 backdrop-blur-sm`}
              >
                {getStatusLabel(workOrder.status)}
              </span>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold border ${getPriorityColor(
                  workOrder.priority,
                )}`}
              >
                Prioridade:{" "}
                {workOrder.priority === "URGENT"
                  ? "Urgente"
                  : workOrder.priority === "HIGH"
                    ? "Alta"
                    : workOrder.priority === "MEDIUM"
                      ? "Média"
                      : "Baixa"}
              </span>
              {isOverdue && (
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-600 text-white">
                  ⚠️ ATRASADA
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Botões de Ação */}
        <div className="mt-4 flex flex-wrap gap-3">
          {workOrder.status === "PENDING" && (
            <button
              onClick={handleStartWorkOrder}
              className="flex items-center gap-2 px-5 py-2 bg-[hsl(var(--secondary))] text-white rounded-lg font-semibold hover:bg-[hsl(var(--secondary))]/90 transition-colors text-sm"
            >
              <IoPlayCircleOutline size={20} />
              Começar O.S
            </button>
          )}
          {workOrder.status === "IN_PROGRESS" && (
            <button
              onClick={() => setShowFinishModal(true)}
              className="flex items-center gap-2 px-5 py-2 bg-[hsl(var(--secondary))] text-white rounded-lg font-semibold hover:bg-[hsl(var(--secondary))]/90 transition-colors text-sm"
            >
              <IoCheckmarkDoneCircle size={20} />
              Enviar para Validação
            </button>
          )}
          {workOrder.status === "AWAITING_VALIDATION" && (
            <div className="flex items-center gap-2 px-5 py-2 bg-purple-100 text-purple-700 rounded-lg font-semibold text-sm">
              <IoTimeOutline size={20} className="flex-shrink-0" />
              Aguardando aprovação da gerência
            </div>
          )}
        </div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {/* Map - ONDE IR */}
        {workOrder.items.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <div className="flex items-center gap-2 mb-4">
              <IoLocationOutline className="text-2xl text-[hsl(var(--primary))]" />
              <div>
                <h2 className="text-lg font-bold text-gray-800">
                  Localização das Ocorrências
                </h2>
                <p className="text-sm text-gray-600">
                  {workOrder.items.length}{" "}
                  {workOrder.items.length === 1 ? "local" : "locais"} para
                  atender
                </p>
              </div>
            </div>
            <WorkOrderMap
              occurrences={workOrder.items.map((item) => ({
                id: item.occurrence.id,
                protocol: item.occurrence.protocol,
                address: resolvedAddresses[item.occurrence.id] || item.occurrence.address,
                latitude: item.occurrence.latitude,
                longitude: item.occurrence.longitude,
              }))}
            />
          </motion.div>
        )}

        {/* Occurrences - O QUE FAZER */}
        {workOrder.items.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <div className="mb-4">
              <h2 className="text-lg font-bold text-gray-800">
                Ocorrências Vinculadas
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Problemas reportados que precisam ser resolvidos
              </p>
            </div>

            <div className="space-y-4">
              {workOrder.items.map((item, index) => (
                <div
                  key={item.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow overflow-hidden"
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-8 h-8 rounded-full bg-[hsl(var(--primary))] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                        {index + 1}
                      </span>
                      <div className="min-w-0">
                        <span className="font-semibold text-[hsl(var(--primary))] break-words">
                          {item.occurrence.protocol}
                        </span>
                        <p className="text-xs text-gray-500">
                          Reportado {getTimeAgo(item.occurrence.createdAt)}
                        </p>
                      </div>
                    </div>
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold whitespace-nowrap flex-shrink-0 ${
                        item.occurrence.status === "IN_PROGRESS"
                          ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {getStatusLabel(item.occurrence.status)}
                    </span>
                  </div>

                  <div className="space-y-3 mt-3">
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide w-20 flex-shrink-0 pt-0.5">Categoria</span>
                      <p className="text-sm font-medium text-gray-800 break-words">
                        {item.occurrence.category.name} • {item.occurrence.subcategory.name}
                      </p>
                    </div>

                    <div className="border-t border-gray-100 pt-3">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Descrição</span>
                      <p className="text-sm text-gray-700 break-words leading-relaxed">
                        {item.occurrence.description}
                      </p>
                    </div>

                    <div className="border-t border-gray-100 pt-3 flex items-start gap-2">
                      <IoLocationOutline className="flex-shrink-0 mt-0.5 text-[hsl(var(--primary))]" />
                      <span className="text-sm text-gray-700 break-words min-w-0">{resolvedAddresses[item.occurrence.id] || item.occurrence.address}</span>
                    </div>

                    {/* Botão de Navegação */}
                    {item.occurrence.latitude && item.occurrence.longitude && (
                      <div className="relative nav-menu-container">
                        <button
                          onClick={() =>
                            setShowNavMenu(
                              showNavMenu === item.id ? null : item.id,
                            )
                          }
                          className="flex items-center gap-2 px-4 py-2 bg-[hsl(var(--secondary))] text-white rounded-lg hover:bg-[hsl(var(--secondary))]/90 transition-colors text-sm font-medium"
                        >
                          <IoNavigateOutline size={18} />
                          Começar Navegação
                        </button>

                        {/* Menu de Opções de Navegação */}
                        {showNavMenu === item.id && (
                          <div className="absolute z-10 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
                            <div className="p-2 bg-gray-50 border-b border-gray-200">
                              <p className="text-xs font-semibold text-gray-700">
                                Escolha o app de navegação:
                              </p>
                            </div>
                            <div className="py-1">
                              <button
                                onClick={() =>
                                  openNavigation(
                                    item.occurrence.latitude!,
                                    item.occurrence.longitude!,
                                    item.occurrence.address,
                                    "google",
                                  )
                                }
                                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                              >
                                <div className="w-8 h-8 bg-[hsl(var(--primary))] rounded-lg flex items-center justify-center">
                                  <IoNavigateOutline
                                    className="text-white"
                                    size={18}
                                  />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-gray-800">
                                    Google Maps
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Web e App
                                  </p>
                                </div>
                              </button>

                              <button
                                onClick={() =>
                                  openNavigation(
                                    item.occurrence.latitude!,
                                    item.occurrence.longitude!,
                                    item.occurrence.address,
                                    "waze",
                                  )
                                }
                                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                              >
                                <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center">
                                  <IoNavigateOutline
                                    className="text-white"
                                    size={18}
                                  />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-gray-800">
                                    Waze
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    App e Web
                                  </p>
                                </div>
                              </button>

                              {isMobile() && (
                                <button
                                  onClick={() =>
                                    openNavigation(
                                      item.occurrence.latitude!,
                                      item.occurrence.longitude!,
                                      item.occurrence.address,
                                      "apple",
                                    )
                                  }
                                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                                >
                                  <div className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center">
                                    <IoNavigateOutline
                                      className="text-white"
                                      size={18}
                                    />
                                  </div>
                                  <div>
                                    <p className="text-sm font-semibold text-gray-800">
                                      Apple Maps
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      iOS e macOS
                                    </p>
                                  </div>
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {item.occurrence.media.length > 0 && (() => {
                    const imageMedia = item.occurrence.media.filter((m: any) => m.type === "IMAGE");
                    return imageMedia.length > 0 ? (
                      <div className="mt-3 flex gap-2 flex-wrap">
                        {imageMedia.slice(0, 3).map((media: any, mediaIdx: number) => (
                          <button
                            key={media.id}
                            onClick={() => openLightbox(item.occurrence.media, mediaIdx)}
                            className="w-20 h-20 rounded-lg bg-gray-200 overflow-hidden cursor-pointer hover:ring-2 hover:ring-[hsl(var(--primary))] transition-all"
                          >
                            <img
                              src={`${import.meta.env.VITE_BUCKET_URL}/${media.key}`}
                              alt="Foto da ocorrência"
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                        {imageMedia.length > 3 && (
                          <button
                            onClick={() => openLightbox(item.occurrence.media, 3)}
                            className="w-20 h-20 rounded-lg bg-gray-300 flex items-center justify-center text-sm font-semibold text-gray-700 cursor-pointer hover:ring-2 hover:ring-[hsl(var(--primary))] transition-all"
                          >
                            +{imageMedia.length - 3}
                          </button>
                        )}
                      </div>
                    ) : null;
                  })()}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Info Card - DETALHES ADMINISTRATIVOS */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 shadow-sm"
        >
          <h2 className="text-lg font-bold text-gray-800 mb-4">
            Informações da OS
          </h2>

          <div className="space-y-5">
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block">
                Descrição
              </label>
              {(() => {
                const sections = workOrder.description.split(/\n\n+/);
                return sections.map((section, idx) => {
                  const trimmed = section.trim();
                  if (!trimmed) return null;

                  // Check if section has a "Label: value" pattern on first line
                  const lines = trimmed.split('\n');
                  const labelMatch = lines[0].match(/^(.+?):\s*(.*)$/);

                  if (labelMatch && lines.length === 1) {
                    // Single line "Label: Value"
                    return (
                      <div key={idx} className={`${idx > 0 ? 'border-t border-gray-200 pt-3' : ''}`}>
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{labelMatch[1]}</span>
                        <p className="text-sm text-gray-800 break-words mt-0.5">{labelMatch[2]}</p>
                      </div>
                    );
                  }

                  if (labelMatch && lines.length > 1) {
                    // Multi-line with label on first line (e.g. "Descrição da ocorrência:\nText")
                    const label = labelMatch[1] + (labelMatch[2] ? `: ${labelMatch[2]}` : '');
                    const body = lines.slice(1).join('\n').trim();
                    return (
                      <div key={idx} className={`${idx > 0 ? 'border-t border-gray-200 pt-3' : ''}`}>
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
                        <p className="text-sm text-gray-800 break-words mt-0.5 leading-relaxed whitespace-pre-wrap">{body}</p>
                      </div>
                    );
                  }

                  // Plain text section (e.g. the opening sentence)
                  return (
                    <div key={idx} className={`${idx > 0 ? 'border-t border-gray-200 pt-3' : ''}`}>
                      <p className="text-sm text-gray-800 break-words leading-relaxed whitespace-pre-wrap">{trimmed}</p>
                    </div>
                  );
                });
              })()}
            </div>

            <div className="border-t border-gray-100 pt-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
                    <IoPersonOutline className="inline mr-1" />
                    Atribuído para
                  </label>
                  <p className="text-gray-800 break-words">
                    {workOrder.assignedTo.firstName &&
                    workOrder.assignedTo.lastName
                      ? `${workOrder.assignedTo.firstName} ${workOrder.assignedTo.lastName}`
                      : workOrder.assignedTo.email}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
                    <IoPeopleOutline className="inline mr-1" />
                    Equipe
                  </label>
                  <p className="text-gray-800">
                    {workOrder.assignedTeam.name} -{" "}
                    {workOrder.assignedTeam.regional}
                  </p>
                </div>

                {workOrder.estimatedStartDate && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
                      <IoCalendarOutline className="inline mr-1" />
                      Início Previsto
                    </label>
                    <p className="text-gray-800">
                      {new Date(workOrder.estimatedStartDate).toLocaleString(
                        "pt-BR",
                      )}
                    </p>
                  </div>
                )}

                {workOrder.estimatedEndDate && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
                      <IoCalendarOutline className="inline mr-1" />
                      Prazo
                    </label>
                    <p
                      className={`font-semibold ${isOverdue ? "text-red-600" : "text-gray-800"}`}
                    >
                      {new Date(workOrder.estimatedEndDate).toLocaleString(
                        "pt-BR",
                      )}
                    </p>
                  </div>
                )}

                {workOrder.actualStartDate && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
                      <IoCheckmarkCircle className="inline mr-1 text-[hsl(var(--primary))]" />
                      Iniciado em
                    </label>
                    <p className="text-gray-800">
                      {new Date(workOrder.actualStartDate).toLocaleString(
                        "pt-BR",
                      )}
                    </p>
                  </div>
                )}

                {workOrder.actualEndDate && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
                      <IoCheckmarkCircle className="inline mr-1 text-[hsl(var(--primary))]" />
                      Concluído em
                    </label>
                    <p className="text-gray-800">
                      {new Date(workOrder.actualEndDate).toLocaleString("pt-BR")}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-gray-100 pt-5">
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
                  Setor Responsável
                </label>
                <p className="text-gray-800 break-words">
                  {workOrder.sector.name} ({workOrder.sector.acronym})
                </p>
                <p className="text-sm text-gray-600 mt-1 break-words">
                  {workOrder.sector.email} • {workOrder.sector.phone}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Chat Interno - COMUNICAÇÃO */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-6 shadow-sm"
        >
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <IoChatbubbleEllipsesOutline className="text-xl" />
            Comentários Internos por Ocorrência
          </h2>

          {/* Abas de Ocorrências */}
          {workOrder.items.length > 0 && (
            <div className="mb-4 border-b border-gray-200">
              <div className="flex gap-2 overflow-x-auto pb-2">
                {workOrder.items.map((item, index) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveCommentTab(item.occurrence.id)}
                    className={`px-4 py-2 rounded-t-lg font-medium text-sm whitespace-nowrap transition-colors ${
                      activeCommentTab === item.occurrence.id
                        ? "bg-[hsl(var(--primary))] text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {index + 1}. {item.occurrence.protocol}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!activeCommentTab && workOrder.items.length > 0 && (
            <div className="text-center text-gray-500 text-sm py-8 bg-gray-50 rounded-lg">
              👆 Selecione uma ocorrência acima para ver e adicionar comentários
            </div>
          )}

          {activeCommentTab && (
            <div className="flex flex-col">
              {/* Lista de comentários */}
              <div ref={chatContainerRef} className="space-y-4 mb-4 max-h-[500px] overflow-y-auto pr-1">
                {commentsData && commentsData.data.length > 0 ? (
                  [...commentsData.data]
                    .sort(
                      (a, b) =>
                        new Date(a.createdAt).getTime() -
                        new Date(b.createdAt).getTime(),
                    )
                    .map((comment: Comment) => {
                      const isMe =
                        comment.author.id === currentUser?.id;
                      const authorName =
                        comment.author.firstName && comment.author.lastName
                          ? `${comment.author.firstName} ${comment.author.lastName}`
                          : "Usuário";
                      const initial = (
                        comment.author.firstName?.[0] ||
                        "U"
                      ).toUpperCase();
                      const roleLabel =
                        comment.author.role === "ADMIN"
                          ? "Administrador"
                          : comment.author.role === "AGENT"
                            ? "Agente"
                            : comment.author.role === "SECTOR_MEMBER"
                              ? "Membro de Setor"
                              : comment.author.role;
                      const dateStr = new Date(
                        comment.createdAt,
                      ).toLocaleString("pt-BR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      });

                      return (
                        <div
                          key={comment.id}
                          className={`flex ${isMe ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[85%] rounded-xl px-4 py-3 border ${
                              isMe
                                ? "bg-[hsl(var(--primary))]/5 border-[hsl(var(--primary))]/20"
                                : "bg-white border-gray-200"
                            }`}
                          >
                            {/* Header: avatar + nome + role + data */}
                            <div className="flex items-center gap-2.5 mb-2">
                              <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold text-white flex-shrink-0 ${
                                  isMe
                                    ? "bg-[hsl(var(--primary))]"
                                    : "bg-gray-400"
                                }`}
                              >
                                {initial}
                              </div>
                              <div className="flex items-center gap-2 flex-wrap min-w-0">
                                <span className="font-semibold text-sm text-gray-800 truncate">
                                  {isMe
                                    ? currentUser?.email || authorName
                                    : authorName}
                                </span>
                                <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200 whitespace-nowrap">
                                  {roleLabel}
                                </span>
                                <span className="text-xs text-gray-400 whitespace-nowrap">
                                  {dateStr}
                                </span>
                              </div>
                            </div>
                            {/* Conteúdo */}
                            <p className="text-sm text-gray-700 whitespace-pre-wrap break-words pl-[42px]">
                              {comment.content}
                            </p>
                            {comment.attachments.length > 0 && (
                              <div className="mt-2 flex gap-2 flex-wrap pl-[42px]">
                                {comment.attachments.map((attachment) => (
                                  <a
                                    key={attachment.id}
                                    href={`${import.meta.env.VITE_BUCKET_URL}/${attachment.key}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 text-xs transition-colors"
                                  >
                                    <IoDocumentAttachOutline className="text-gray-500" />
                                    <span className="text-gray-700 truncate max-w-[150px]">
                                      {attachment.fileName}
                                    </span>
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                ) : (
                  <div className="text-center text-gray-500 text-sm py-8">
                    Nenhum comentário interno ainda
                  </div>
                )}

              </div>

              {/* Arquivos anexados */}
              {messageFiles.length > 0 && (
                <div className="flex gap-2 flex-wrap mb-2">
                  {messageFiles.map((file: File, index: number) => (
                    <div
                      key={index}
                      className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-lg border border-gray-200 text-xs"
                    >
                      <IoDocumentAttachOutline className="text-gray-500" />
                      <span className="text-gray-700 max-w-[150px] truncate">
                        {file.name}
                      </span>
                      <button
                        onClick={() => handleRemoveFile(index)}
                        className="p-0.5 hover:bg-gray-200 rounded-full"
                        disabled={isSubmittingMessage}
                      >
                        <IoCloseOutline className="text-sm text-gray-600" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Input de mensagem */}
              <div className="border-t border-gray-200 pt-3">
                <div className="flex items-end gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={isSubmittingMessage}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                    disabled={isSubmittingMessage || !activeCommentTab}
                    title="Anexar arquivo"
                  >
                    <IoAttachOutline className="text-xl" />
                  </button>

                  <div className="flex-1">
                    <textarea
                      value={messageContent}
                      onChange={(e) => setMessageContent(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          if (messageContent.trim() && activeCommentTab) {
                            handleSubmitMessage();
                          }
                        }
                      }}
                      placeholder="Escreva um comentário interno que só será visível para administradores"
                      rows={1}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/30 focus:border-[hsl(var(--primary))] resize-none max-h-[100px]"
                      disabled={isSubmittingMessage || !activeCommentTab}
                    />
                  </div>

                  <button
                    onClick={handleSubmitMessage}
                    disabled={
                      isSubmittingMessage ||
                      !messageContent.trim() ||
                      !activeCommentTab
                    }
                    className="p-2 text-[hsl(var(--secondary))] hover:text-[hsl(var(--secondary))]/80 transition-colors disabled:opacity-30"
                    title="Enviar"
                  >
                    {isSubmittingMessage ? (
                      <Spinner className="w-5 h-5" />
                    ) : (
                      <IoSendOutline className="text-xl" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* History - REGISTRO DE AÇÕES */}
        {workOrder.history.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <div className="mb-4">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <IoDocumentTextOutline className="text-xl" />
                Histórico
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Registro cronológico de todas as ações
              </p>
            </div>

            <div className="space-y-3">
              {workOrder.history.map((entry, index) => (
                <div
                  key={entry.id}
                  className="flex gap-3 pb-3 border-b border-gray-200 last:border-b-0 last:pb-0"
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[hsl(var(--primary))]/10 flex items-center justify-center">
                    <span className="text-xs font-bold text-[hsl(var(--primary))]">
                      {index + 1}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800">
                      {entry.action === "created" && "Ordem de Serviço Criada"}
                      {entry.action === "status_changed" && "Status Alterado"}
                      {entry.action === "assigned" && "Atribuição Alterada"}
                      {entry.action === "completed" && "Concluída"}
                    </p>
                    {entry.fromValue && entry.toValue && (
                      <p className="text-sm text-gray-600 mt-1 break-words">
                        {translateHistoryValue(entry.fromValue)} →{" "}
                        {translateHistoryValue(entry.toValue)}
                      </p>
                    )}
                    {entry.toValue && !entry.fromValue && (
                      <p className="text-sm text-gray-600 mt-1">
                        {translateHistoryValue(entry.toValue)}
                      </p>
                    )}
                    {entry.justification && (
                      <p className="text-sm text-gray-600 italic mt-1 break-words">
                        "{entry.justification}"
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                      <IoTimeOutline />
                      <span>{getTimeAgo(entry.performedAt)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Modal de Envio para Validação */}
      {showFinishModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <IoCheckmarkDoneCircle className="text-[hsl(var(--primary))]" />
                  Enviar para Validação
                </h2>
                <button
                  onClick={() => setShowFinishModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  disabled={isSubmittingFinish}
                >
                  <IoCloseOutline className="text-2xl text-gray-600" />
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Após o envio, a gerência irá validar a execução do serviço. Você será notificado quando for aprovado ou se precisar de ajustes.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Descrição da Execução *
                  </label>
                  <textarea
                    value={finishDescription}
                    onChange={(e) => setFinishDescription(e.target.value)}
                    placeholder="Descreva brevemente o serviço executado, materiais utilizados, etc..."
                    className="w-full min-h-[120px] p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent resize-none"
                    disabled={isSubmittingFinish}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Comprovantes de Execução *
                  </label>

                  {finishFiles.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {finishFiles.map((file: File, index: number) => (
                        <div
                          key={index}
                          className="flex items-center justify-between gap-2 p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <IoDocumentAttachOutline className="text-lg text-gray-500 flex-shrink-0" />
                            <span className="text-sm text-gray-700 truncate">
                              {file.name}
                            </span>
                            <span className="text-xs text-gray-500 flex-shrink-0">
                              ({(file.size / 1024).toFixed(0)} KB)
                            </span>
                          </div>
                          <button
                            onClick={() => handleRemoveFinishFile(index)}
                            className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                            disabled={isSubmittingFinish}
                          >
                            <IoCloseOutline className="text-lg text-gray-600" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <input
                    ref={finishFileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleFinishFileSelect}
                    className="hidden"
                    disabled={isSubmittingFinish}
                  />
                  <button
                    onClick={() => finishFileInputRef.current?.click()}
                    className="w-full flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/5 transition-colors"
                    disabled={isSubmittingFinish}
                  >
                    <IoAttachOutline className="text-xl text-gray-600" />
                    <span className="text-sm font-medium text-gray-600">
                      {finishFiles.length === 0
                        ? "Adicionar comprovantes (fotos e vídeos)"
                        : "Adicionar mais comprovantes"}
                    </span>
                  </button>
                  <p className="text-xs text-gray-500 mt-2">
                    Anexe fotos ou vídeos do serviço executado. Vídeos devem ter no máximo <strong>10 segundos</strong> de duração. (máximo 20MB cada)
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowFinishModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                  disabled={isSubmittingFinish}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleFinishWorkOrder}
                  disabled={
                    isSubmittingFinish ||
                    !finishDescription.trim() ||
                    finishFiles.length === 0
                  }
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] text-white rounded-lg font-semibold hover:from-[hsl(var(--secondary))] hover:to-[hsl(var(--primary))] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmittingFinish ? (
                    <>
                      <Spinner className="w-5 h-5" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <IoCheckmarkDoneCircle size={20} />
                      Enviar para Validação
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Lightbox de Imagens */}
      <ImageLightbox
        images={lightboxImages}
        initialIndex={lightboxIndex}
        isOpen={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
      />
    </div>
  );
};
